import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentEvents, MemoryUpdatedEvent } from '@dreamai/types';
import api from '../../../api';
import { usePusher } from '../../../contexts/PusherContext';

interface MemoryCountResponse {
  success: boolean;
  count: number;
  hasUnapproved: boolean;
}

/**
 * Hook to fetch unconfirmed memory count and reactively update via Pusher
 * Used by the navigation to display a notification badge on the Memories link
 */
export const useUnconfirmedMemoryCount = () => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const companyChannel = pusherContext?.pusherChannel;
  const [unconfirmedCount, setUnconfirmedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchCount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<MemoryCountResponse>('/memory-count', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.hasUnapproved) {
        setUnconfirmedCount(response.data.count);
      } else {
        setUnconfirmedCount(0);
      }
    } catch (error) {
      console.error('Error fetching unconfirmed memory count:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  // Initial fetch
  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Listen for Pusher memory-updated events to refresh count
  useEffect(() => {
    if (!companyChannel) return;

    const handleMemoryUpdated = (data: MemoryUpdatedEvent) => {
      fetchCount();
    };

    companyChannel.bind(AgentEvents.MEMORY_UPDATED, handleMemoryUpdated);

    return () => {
      companyChannel.unbind(AgentEvents.MEMORY_UPDATED, handleMemoryUpdated);
    };
  }, [companyChannel, fetchCount]);

  return { unconfirmedCount, loading, refresh: fetchCount };
};
